import { LazoCheckbox } from "@/components/common/form/LazoCheckbox";
import { InfoContainer } from "@/components/data-room/settings/VirtualDataRoomSharingSection";
import { InformationRowContainer } from "@/components/lazo-one-settings-components/profile-components/CompanyInformation";
import { InformationFormContainer } from "@/components/lazo-one-settings-components/profile-components/PersonalInformationEditForm";
import WorkspaceTextfield from "@/components/signup-components/individual/WorkspaceTextfield";
import CommonButton from "@/components/utils/CommonButton";
import CommonFormControl from "@/components/utils/CommonFormControl";
import CommonTextField from "@/components/utils/CommonTextField";
import CheckCircleIcon from "@/components/utils/icons/CheckCircleIcon";
import LazoModal from "@/components/utils/LazoModal";
import LazoTooltip from "@/components/utils/LazoTooltip";
import { CommonStyledSelect, CommonStyledSelectMenuProps } from "@/components/utils/SelectStyles";
import { Colors } from "@/enums/Colors";
import { LazoAccountType } from "@/enums/LazoAccountType";
import { LazoCompanyAccountType } from "@/enums/LazoCompanyAccountType";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import UserRole from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/UserRole";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { CompanyOnboarding } from "@/interfaces/onboarding-interfaces/CompanyOnboarding";
import { UserOnboarding } from "@/interfaces/onboarding-interfaces/UserOnboarding";
import { LazoUser } from "@/models/LazoUser";
import { Workspace } from "@/models/LazoWorkspace";
import LazoRoleService from "@/services/LazoRoleService";
import LazoUserService from "@/services/LazoUserService";
import WorkspaceService from "@/services/workspaces/WorkspaceService";
import { LazoInvitation } from "@/types/LazoInvitation";
import { LazoInvitationType } from "@/types/LazoInvitationType";
import { AddOutlined, ExpandMore, InfoOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  checkboxClasses,
  FormLabel,
  ListItem,
  MenuItem,
  outlinedInputClasses,
  Skeleton,
  styled,
  svgIconClasses,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Controller, FieldError, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const REGEX_EMAIL_PATTERN =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/i;

function RoleSelect({ role, setRole, roles }: { role: string; setRole: Dispatch<SetStateAction<string>>; roles: UserRole[] }) {
  roles = roles.filter((r) => !r.name.includes("kam"));

  return (
    <CommonStyledSelect
      value={role}
      onChange={(e) => setRole(e.target.value as string)}
      IconComponent={ExpandMore}
      MenuProps={CommonStyledSelectMenuProps}
    >
      {roles.map((r) => {
        const name = r.name.split("-").pop()!;
        return (
          <MenuItem key={r.externalId} value={r.externalId!}>
            <Typography>{name.charAt(0).toUpperCase() + name.slice(1)}</Typography>
          </MenuItem>
        );
      })}
    </CommonStyledSelect>
  );
}

const debouncer = new Debouncer(140);
const emailValueDebouncer = new Debouncer(250);

interface Props {
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  handleNextStep: () => void;
  handlePreviousStep: () => void;
  ownerUserData: UserOnboarding;
  setOwnerUserData: React.Dispatch<React.SetStateAction<UserOnboarding>>;
  userMembers: LazoInvitation[];
  setUserMembers: React.Dispatch<React.SetStateAction<LazoInvitation[]>>;
  companyData: CompanyOnboarding;
  handleCreateCompany: () => void;
  isOpen?: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  accountType: LazoCompanyAccountType;
  setWorkspaceName: React.Dispatch<React.SetStateAction<string>>;
  creating: boolean;
}

export const OwnerCompanyUserForm = (props: Props) => {
  const { t } = useTranslation(["common", "customer"]);
  const [role, setRole] = useState<string>("");
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [email, setEmail] = useState<string>("");
  const [assignPendingTasksOnAcceptance, setAssignPendingTasksOnAcceptance] = useState<boolean>(false);

  const [suggestedUsers, setSuggestedUsers] = useState<LazoUser[] | null>(null);
  const [addedUser, setAddedUser] = useState<LazoUser>();
  const [emailValue, setEmailValue] = useState<string | undefined>(undefined);
  const [loadingSuggestedUsers, setLoadingSuggestedUsers] = useState<boolean>(false);
  const [checking, setChecking] = React.useState<boolean>(false);
  const [available, setAvailable] = React.useState<boolean>(false);
  const [takenError, setTakenError] = React.useState<FieldError>(null);
  const [useExistingWorkspace, setUseExistingWorkspace] = useState<boolean>(false);
  const [userWorkspaces, setUserWorkspaces] = useState<Workspace[]>([]);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState<boolean>(false);

  const fetchWorkspaces = async () => {
    setIsLoadingWorkspaces(true);

    await WorkspaceService.getAllWorkspaces().then((response) => {
      response.onSuccess((workspaces) => {
        setWorkspaces(workspaces);
        setIsLoadingWorkspaces(false);
      });
      response.onError(() => {
        NotificationService.error("Error fetching all workspaces");
      });
    });
  };

  useMemo(() => {
    fetchWorkspaces();
  }, []);

  useEffect(() => {
    setSuggestedUsers([]);

    if (userWorkspaces.length > 0) {
      setUserWorkspaces([]);
    }

    async function fetchSuggestedUsers(term: string) {
      if (!term) return;
      LazoUserService.searchUserSuggestionsByTermUnpaged(term).then((r) => {
        r.onSuccess((page) => {
          let content = page.content;
          setSuggestedUsers(content);
        });
        r.onError(() => {});
      });
    }

    setLoadingSuggestedUsers(true);
    emailValueDebouncer.executeLastCall(() =>
      fetchSuggestedUsers(emailValue).finally(() => {
        setLoadingSuggestedUsers(false);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue]);

  useEffect(() => {
    LazoRoleService.getAllPortalRoles().then((r) => {
      r.onSuccess((content) => {
        setRoles(content);
        if (content[0]) {
          setRole(content[0].externalId!);
        }
      });
      r.onError((e) => console.log("There was an error getting the roles. Message: ", e));
    });
  }, []);

  useEffect(() => {
    if (!open) {
      closeModal();
    }
  }, [open]);

  function registerUser(data: {
    title: string;
    hasAcceptedTerms: boolean;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    repeatedPassword: string;
    accountType: LazoAccountType;
    isOAuthSignUp: boolean;
    workspaceName: string;
  }) {
    let userCreation: UserOnboarding = {
      title: data.title,
      hasAcceptedTerms: true,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      password: data.password,
      repeatedPassword: data.password,
      accountType: data.accountType,
      isOAuthSignUp: false,
    };

    props.setWorkspaceName(data.workspaceName);
    props.setOwnerUserData(userCreation);
  }

  const handleAddCompanyMembers = (invitation: LazoInvitation) => {
    props.setUserMembers([...props.userMembers, invitation]);
  };

  const handleRemoveMember = (memberEmail: string) => {
    props.setUserMembers(props.userMembers.filter((member) => member.email !== memberEmail));
  };

  const { register, setValue, handleSubmit, control, reset, watch, unregister } = useForm({ mode: "onChange" });
  const closeModal = () => {
    props.setIsOpen(false);
    reset();
  };

  async function handleAddSuggestion(u: LazoUser) {
    setAddedUser(u);

    setIsLoadingWorkspaces(true);
    await WorkspaceService.getWorkspacesByUserExternalId(u.user.externalId)
      .then((r) => {
        r.onSuccess((userExistingWorkspaces) => {
          setUserWorkspaces(userExistingWorkspaces);
        });
      })
      .finally(() => setIsLoadingWorkspaces(false));
  }

  useEffect(() => {
    // Don't reset the fields if the user does not exist
    if (!emailValue || emailValue.length === 0 || addedUser?.user.externalId === null) {
      return;
    }

    setValue("firstName", addedUser?.user?.firstName || "");
    setValue("lastName", addedUser?.user?.lastName || "");
    setValue("title", addedUser?.title || "");
    setValue("email", addedUser?.user.email || undefined);
  }, [addedUser]);

  const formWorkspaceName = watch("workspaceName");
  useEffect(() => {
    setChecking(true);
    debouncer.executeLastCall(() => {
      WorkspaceService.checkIfSlugIsTaken(formWorkspaceName)
        .then((r) => {
          r.onSuccess((isTaken) => {
            if (isTaken) {
              setAvailable(false);
              setTakenError({
                type: "value",
                message: "This Workspace name is already in use",
              });
            } else {
              setTakenError(null);
              setAvailable(true);
            }
          });
        })
        .finally(() => setChecking(false));
    });
  }, [formWorkspaceName]);

  function renderSuggestion(props: React.HTMLAttributes<HTMLLIElement>, u: LazoUser) {
    if (u.user.externalId === null) {
      return (
        <ListItem {...props} style={{ padding: "1.25rem 0.75rem" }}>
          <Box onClick={() => handleAddSuggestion(u)}>Add: {u.user.email}</Box>
        </ListItem>
      );
    }

    return (
      <ListItem {...props} style={{ padding: "1.25rem 0.75rem" }}>
        <InfoContainer user={u.user} onClick={() => handleAddSuggestion(u)} />
      </ListItem>
    );
  }

  function onUseExistingWorkspaceChange() {
    setUseExistingWorkspace((prev) => !prev);
    unregister("workspaceName");
    setValue("workspaceName", "");
  }

  return (
    <>
      <CommonButton
        sx={{
          height: "2.5rem",
          whiteSpace: "nowrap",
          paddingInline: "16px",
          marginRight: "0.875rem",
          borderRadius: "0.5rem",
        }}
        endIcon={<AddOutlined />}
        onClick={() => props.setIsOpen(true)}
        disabled={props.creating}
      >
        <Typography fontWeight={600}>Create User</Typography>
      </CommonButton>

      <LazoModal
        open={props.isOpen}
        handleClose={() => props.setIsOpen(false)}
        title="Create Customer Owner user or Select Existent"
        sx={{
          width: "650px",
          "& .content-container": {
            height: props.accountType === LazoCompanyAccountType.COMPANY ? "27.25rem !important" : "21.25rem !important",
            overflowY: "auto",
            "& > div": {
              height: "inherit",
            },
          },
        }}
        buttons={
          <>
            <CommonButton purpleBorderVariant disabled={props.creating} onClick={props.handlePreviousStep}>
              Go back
            </CommonButton>
            <CommonButton disabled={props.creating} onClick={handleSubmit(registerUser)}>
              {props.creating ? (
                <>
                  <Spinner size={"1.25rem"} />
                </>
              ) : (
                <Typography>Create</Typography>
              )}
            </CommonButton>
          </>
        }
      >
        <>
          <InformationFormContainer>
            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ width: "50%" }}>
                <FormLabel>First Name</FormLabel>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name={"firstName"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField placeholder={error && "First name required"} onChange={onChange} error={!!error} value={value || ""} />
                  )}
                />
              </CommonFormControl>
              <CommonFormControl sx={{ width: "50%" }}>
                <FormLabel>Last Name</FormLabel>
                <Controller
                  control={control}
                  name={"lastName"}
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField placeholder={error && "Last name required"} onChange={onChange} error={!!error} value={value || ""} />
                  )}
                />
              </CommonFormControl>
            </InformationRowContainer>
            <CommonFormControl>
              <FormLabel>Title</FormLabel>
              <Controller
                name="title"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    placeholder={error ? "Title required" : "Enter title"}
                    error={!!error}
                    onChange={onChange}
                    value={value || ""}
                  />
                )}
              />
            </CommonFormControl>

            <CommonFormControl>
              <FormLabel>Email</FormLabel>
              <Controller
                control={control}
                name={"email"}
                render={({ fieldState: { error } }) => (
                  <Autocomplete
                    limitTags={2}
                    options={suggestedUsers || []}
                    getOptionLabel={(opt: LazoUser) => opt.user?.email || opt.user?.externalId || ""}
                    loading={loadingSuggestedUsers}
                    renderOption={(props, option) => renderSuggestion(props, option)}
                    sx={AutoCompleteSx}
                    slotProps={{ paper: { sx: SuggestionPaperSx } }}
                    value={addedUser}
                    isOptionEqualToValue={(opt, val) => opt.externalId === val.externalId}
                    filterOptions={(options, params) => {
                      if (!params.inputValue || params.inputValue === "" || loadingSuggestedUsers) {
                        return options;
                      }

                      // If the email does not belong to any user, append the option to add it
                      const isExisting = options.some((opt) => opt.user.email === params.inputValue);
                      if (!isExisting) {
                        const addOption = {
                          user: {
                            email: params.inputValue,
                            externalId: null,
                          },
                        } as LazoUser;
                        return [addOption, ...options];
                      }

                      return options;
                    }}
                    onChange={(_, v) => {
                      if (typeof v === "string") {
                        const newUser: LazoUser = {
                          externalId: v,
                          // @ts-ignore - This is pretty shittily typed
                          user: { email: v },
                        };
                        handleAddSuggestion(newUser);
                      } else {
                        setAddedUser(v as LazoUser);
                      }
                    }}
                    inputValue={emailValue}
                    onInputChange={(_, v) => setEmailValue(v)}
                    renderInput={(params) => (
                      <TextField
                        autoComplete="off"
                        type="email"
                        {...register("email", { required: true, pattern: REGEX_EMAIL_PATTERN })}
                        {...params}
                        placeholder={error ? "Email required" : "Select or type an email from an existing owner"}
                      />
                    )}
                  />
                )}
              />
            </CommonFormControl>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StyledCheckbox checked={useExistingWorkspace} onChange={onUseExistingWorkspaceChange} />
              <Typography
                sx={{
                  color: Colors.ELEGANT_BLACK,
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  lineHeight: "1rem",
                }}
              >
                Use existing workspace
              </Typography>
            </Box>

            <InformationRowContainer gap={"inherit"}>
              {!useExistingWorkspace ? (
                <CommonFormControl>
                  <FormLabel>Choose a name for the workspace</FormLabel>
                  <Controller
                    control={control}
                    name="workspaceName"
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <WorkspaceTextfield
                        {...register("workspaceName", { required: true, pattern: /^[a-z0-9]+$/ })}
                        autoComplete="off"
                        disabled={useExistingWorkspace}
                        textfieldSx={{ width: "430px", height: "44px" }}
                        onChange={field.onChange}
                        value={field.value || ""}
                        error={error || takenError}
                        checkingTaken={checking}
                        available={formWorkspaceName?.length >= 3 && available}
                      />
                    )}
                  />
                </CommonFormControl>
              ) : (
                <CommonFormControl>
                  <FormLabel>Existing workspace (existing owner&apos;s workspaces are checked)</FormLabel>
                  <Controller
                    name="workspaceName"
                    control={control}
                    render={({ field, fieldState: { error } }) => {
                      return !isLoadingWorkspaces ? (
                        <CommonStyledSelect
                          {...register("workspaceName", { required: true })}
                          onChange={field.onChange}
                          value={field.value || ""}
                          error={!!error}
                          disabled={!useExistingWorkspace}
                          MenuProps={CommonStyledSelectMenuProps}
                        >
                          {workspaces
                            .sort((a, b) => a.slug.localeCompare(b.slug))
                            .map((workspace) => (
                              <MenuItem key={workspace.slug} value={workspace.slug}>
                                <Box display="flex" alignItems="center" gap="0.25rem">
                                  <Typography>{workspace.slug}</Typography>
                                  {userWorkspaces.some((userWorkspace) => workspace.slug === userWorkspace.slug) && <CheckCircleIcon />}
                                </Box>
                              </MenuItem>
                            ))}
                        </CommonStyledSelect>
                      ) : (
                        <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: "0.75rem" }} />
                      );
                    }}
                  />
                </CommonFormControl>
              )}
            </InformationRowContainer>

            {!addedUser?.externalId && (
              <CommonFormControl>
                <FormLabel>Password</FormLabel>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name={"password"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      type="password"
                      placeholder={error && "Password required"}
                      onChange={onChange}
                      error={!!error}
                      autoComplete="off"
                      value={value || ""}
                    />
                  )}
                />
              </CommonFormControl>
            )}

            {props.accountType === LazoCompanyAccountType.COMPANY && (
              <>
                <MembersTypo>Add more members. Invitations will be sent to them</MembersTypo>

                <InformationRowContainer gap={"inherit"}>
                  <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                    <InviteViaEmailTypography>Email</InviteViaEmailTypography>
                    <CommonTextField
                      sx={{
                        [`& .${outlinedInputClasses.root}`]: {
                          height: "2.750rem",
                          width: "300px",
                          [`& .${outlinedInputClasses.input}`]: {},
                        },
                      }}
                      placeholder={"Email"}
                      value={email || ""}
                      onChange={(e) => setEmail(e.currentTarget.value)}
                    />
                  </CommonFormControl>

                  <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                    <FormLabel>Role</FormLabel>
                    <RoleSelect role={role} setRole={setRole} roles={roles} />
                  </CommonFormControl>
                </InformationRowContainer>
                <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <LazoCheckbox
                    checked={assignPendingTasksOnAcceptance}
                    onChange={(e) => setAssignPendingTasksOnAcceptance(e.currentTarget.checked)}
                    sx={{
                      width: "1rem",
                      height: "1rem",
                      "&.MuiCheckbox-root": {
                        color: `${Colors.LIGHT_GRAY_DEDEE1}`,
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.15rem",
                        },
                      },
                    }}
                    inputProps={{
                      "aria-label": t(
                        "customer:usersTab.headerActions.assignPendingTasksOnAcceptance",
                        "Assign all pending tasks on acceptance"
                      ) as string,
                    }}
                  />
                  <Typography fontSize={"14px"} fontWeight={"400"}>
                    {t("customer:usersTab.headerActions.assignPendingTasksOnAcceptance", "Assign all pending tasks on acceptance")}
                  </Typography>
                  <LazoTooltip
                    maxWidth="320px"
                    title={
                      <Typography fontSize={"0.75rem"}>
                        {t(
                          "customer:usersTab.headerActions.assignPendingTasksHint",
                          "When enabled, the new user will automatically be added to every task and workflow currently assigned to the company owner the moment they accept the invitation."
                        )}
                      </Typography>
                    }
                  >
                    <InfoOutlined sx={{ fontSize: "1rem", color: Colors.LIGHT_GRAY_8A8E93, cursor: "help" }} />
                  </LazoTooltip>
                </Box>
                <CommonFormControl>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "start",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "1rem",
                      }}
                    >
                      <CommonButton
                        onClick={() => {
                          let invitation: LazoInvitation = {
                            email: email,
                            content: {
                              roleExternalId: role,
                              tenantExternalId: "",
                              type: LazoInvitationType.USER_TO_COMPANY,
                            },
                            initiatorEmail: AuthService.getUser().email,
                            type: LazoInvitationType.USER_TO_COMPANY,
                            assignPendingTasksOnAcceptance,
                          };

                          handleAddCompanyMembers(invitation);
                          setEmail("");
                          setAssignPendingTasksOnAcceptance(false);
                        }}
                        sx={{
                          height: "2.750rem",
                          borderRadius: "0.500rem",
                          width: "300px",
                          marginTop: "0.500rem",
                        }}
                      >
                        Add Member
                      </CommonButton>
                      <span style={{ marginLeft: "2rem" }}>
                        {/* +1 since the owner should be counted*/}
                        <MembersTypo>
                          Members: <b>{props.userMembers.length + 1}</b> <i>(includes the owner)</i>
                        </MembersTypo>
                      </span>
                    </div>
                  </div>
                  {props.userMembers.map((member) => (
                    <div
                      key={member.email}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "10%",
                      }}
                    >
                      <Button onClick={() => handleRemoveMember(member.email)}>
                        <CloseIcon />
                      </Button>
                      {member.email}
                    </div>
                  ))}
                </CommonFormControl>
              </>
            )}
          </InformationFormContainer>
        </>
      </LazoModal>
    </>
  );
};

const InviteViaEmailTypography = styled(Typography)(() => ({
  color: `${Colors.LIGHT_GRAY_8A8E93}`,
  fontWeight: "500",
  fontSize: "0.750rem",
  padding: "0 0 0.250rem 0.500rem",
}));

const MembersTypo = styled(Typography)(() => ({
  color: `${Colors.DARK_GREY_3E3E3E}`,
  fontWeight: "500",
  fontSize: ".875rem",
  padding: "0 0 0.250rem 0.500rem",
}));

const StyledCheckbox = styled(Checkbox)(() => ({
  backgroundColor: "#fff",
  width: "1.000rem",
  height: "1.000rem",
  color: `${Colors.LIGHT_GRAY_DEDEE1}`,
  [`& .${svgIconClasses.root}`]: {
    fontSize: "1.5rem",
  },
  [`&.${checkboxClasses.checked}`]: {
    color: `${Colors.BUTTON_PURPLE}`,
  },
  [`&.${checkboxClasses.disabled}`]: {
    color: `${Colors.ALMOST_WHITE}`,
  },
}));

const AutoCompleteSx: SxProps = {
  "&.MuiAutocomplete-root": {
    "& .MuiAutocomplete-input": {
      padding: 0,
    },
    "& .MuiOutlinedInput-root": {
      gap: "0.25rem",
    },
    "& .MuiAutocomplete-tag": {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      borderRadius: "0.25rem",
      padding: "0.25rem",
      backgroundColor: Colors.ACCENT_BLUE_DESATURATED_CED5FD,
      margin: 0,
      height: "28px",
      "& .MuiChip-label": {
        fontSize: "0.875rem",
        padding: 0,
      },
      "& .MuiChip-deleteIcon": {
        fontSize: "1.25rem",
        margin: 0,
      },
    },
  },
};

const SuggestionPaperSx: SxProps = {
  boxShadow: "0 0 20px 0 rgba(0, 0, 0, 0.1)",
  borderRadius: "0.25rem",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
};
